import { useEffect, useRef, useState } from 'react';

/**
 * useImageSequence — Progressively preloads an image sequence.
 * Prioritizes the first frame for instant visual feedback, 
 * then loads the rest in the background to avoid blocking the main thread.
 *
 * Returns:
 *  - loadedFrames: Set of frame indices that have finished loading
 *  - isLoaded: true when all frames are cached
 *  - loadProgress: 0–100 integer
 */
export function useImageSequence(
  totalFrames: number,
  getFrameSrc: (index: number) => string,
  shouldLoad: boolean = true
) {
  const [loadedFrames, setLoadedFrames] = useState<Set<number>>(new Set());
  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Keep refs for active Image elements so they stay in cache
  const imageCacheRef = useRef<HTMLImageElement[]>([]);

  useEffect(() => {
    // Don't start loading until signaled (lazy-loading gate)
    if (!shouldLoad) return;
    let loadedCount = 0;
    const cache = new Array(totalFrames);
    imageCacheRef.current = cache;

    // Helper to update state
    const markFrameLoaded = (index: number) => {
      loadedCount++;
      setLoadedFrames(prev => {
        const next = new Set(prev);
        next.add(index);
        return next;
      });
      setLoadProgress(Math.round((loadedCount / totalFrames) * 100));

      if (loadedCount === totalFrames) {
        setIsLoaded(true);
      }
    };

    const loadFrame = (index: number): Promise<void> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = getFrameSrc(index);

        const onComplete = () => {
          markFrameLoaded(index);
          resolve();
        };

        img.onload = onComplete;
        img.onerror = onComplete; // Treat errors as complete so we don't stall
        cache[index] = img;
      });
    };

    // Main loading routine
    const startLoading = async () => {
      // 1. Urgently load the absolute first frame to unblock initial render
      await loadFrame(0);

      // 2. Micro-delay the background queue so the browser can download fonts,
      // CSS, and other critical images for the rest of the site first.
      await new Promise(r => setTimeout(r, 200));

      // 3. Load the rest in chunks asynchronously in the background
      // Use small batches to not lock the network/main thread entirely
      const batchSize = 10;
      for (let i = 1; i < totalFrames; i += batchSize) {
        const batch = [];
        for (let j = i; j < Math.min(i + batchSize, totalFrames); j++) {
          batch.push(loadFrame(j));
        }
        await Promise.all(batch);

        // Yield to main thread briefly between batches
        await new Promise(r => setTimeout(r, 10));
      }
    };

    startLoading();

    // Cleanup
    return () => {
      cache.forEach((img) => {
        if (img) {
          img.onload = null;
          img.onerror = null;
        }
      });
    };
  }, [totalFrames, getFrameSrc, shouldLoad]);

  return { loadedFrames, isLoaded, loadProgress };
}
